var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo } from 'react';
import { TRPCClientError } from '@trpc/client';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BlockIcon from '@mui/icons-material/Block';
import { CreatorEventFulfilledDareStatus } from 'root/src/shared/dares/creatorEventFulfilledDare.types';
import { projectAcceptedKey, projectDeliveredKey, projectDeliveryPendingKey, projectPendingKey, projectRejectedKey, } from 'root/src/shared/constants/keys';
import { ACCEPT_DARE_ERROR_CODE_MAP } from 'root/src/shared/dares/dares.constants';
import { getReadableSocialPlatform } from 'root/src/shared/platforms/helpers/getReadablePlatform';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from 'root/src/client/v2/common/components/ui/tooltip';
import { DarePlatformIcon, PlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { buildAssetUrl, assetSize } from 'root/src/client/v2/common/helpers/assetUrl';
import { upscaleBoxArt } from 'root/src/client/v2/_pages/drops/redesign/dropImage';
import { RedesignEditDareModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignEditDareModal';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { Img } from 'root/src/client/v2/common/components/ui/image';
import { ArtworkFrame } from 'root/src/client/v2/common/components/ArtworkFrame';
const STATUS_LABEL = {
    [projectPendingKey]: { label: 'Pending', className: 'border-neon-amber/40 bg-neon-amber/15 text-neon-amber' },
    [projectAcceptedKey]: { label: 'Accepted', className: 'border-neon-cyan/40 bg-neon-cyan/15 text-neon-cyan' },
    [projectDeliveryPendingKey]: { label: 'In review', className: 'border-neon-amber/40 bg-neon-amber/15 text-neon-amber' },
    [projectDeliveredKey]: { label: 'Delivered', className: 'border-neon-green/40 bg-neon-green/15 text-neon-green' },
    [projectRejectedKey]: { label: 'Rejected', className: 'border-neon-pink/40 bg-neon-pink/15 text-neon-pink' },
};
const getProjectLabel = (project) => {
    const expired = new Date(project.expirationDate).getTime() <= Date.now();
    const completed = project.status === projectDeliveredKey;
    if (!completed && expired) {
        return { label: 'Expired', className: 'border-white/[0.06] bg-secondary text-muted-foreground' };
    }
    return STATUS_LABEL[project.status];
};
export const RedesignCommunityDareCard = memo(({ dare, isDareCreator, isAdmin, enableCreatorDares, refetch }) => {
    var _a, _b;
    const isAuthenticated = useIsAuthenticated();
    const redesignModal = useRedesignModal();
    const accept = useAsyncAction();
    const acceptMutation = trpc.dares.acceptEventFulfilledDare.useMutation();
    const isEditable = (isDareCreator && dare.status === CreatorEventFulfilledDareStatus.Enabled) || isAdmin;
    const canBeAccepted = !isDareCreator && !dare.project && isAuthenticated;
    const projectLabel = dare.project ? getProjectLabel(dare.project) : undefined;
    const image = buildAssetUrl(upscaleBoxArt(dare.game.image, '600x800'), { w: 600, q: 80, fmt: 'webp' });
    const onEdit = () => redesignModal.show(<RedesignEditDareModal dare={dare} onEdit={refetch}/>);
    const onAccept = () => {
        void accept.run(() => __awaiter(void 0, void 0, void 0, function* () {
            yield acceptMutation.mutateAsync({ dareId: dare.id });
            yield refetch();
        }), {
            toast: {
                loading: 'Accepting dare...',
                success: 'Dare accepted.',
                error: (caught) => {
                    var _a, _b;
                    if (caught instanceof TRPCClientError) {
                        const errorCode = (_b = (_a = caught.data) === null || _a === void 0 ? void 0 : _a.cause) === null || _b === void 0 ? void 0 : _b.errorCode;
                        if (errorCode === ACCEPT_DARE_ERROR_CODE_MAP.CREATOR_PLATFORM_NOT_CONNECTED) {
                            return 'Connect a creator platform to accept this dare.';
                        }
                        if (errorCode === ACCEPT_DARE_ERROR_CODE_MAP.AUTH_PLATFORM_NOT_CONNECTED && dare.authPlatform) {
                            return `Connect your ${getReadableSocialPlatform(dare.authPlatform)} account to accept this dare.`;
                        }
                    }
                    return caught instanceof Error ? caught.message : 'Could not accept dare';
                },
            },
        });
    };
    return (<article className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-solid border-white/[0.06] bg-card transition-colors hover:border-neon-pink/30">
			{isEditable && enableCreatorDares && (<div className="absolute right-3 top-3 z-20">
					<Tooltip>
						<TooltipTrigger asChild>
							<button type="button" onClick={onEdit} aria-label="Edit dare" className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white shadow-sm transition-opacity hover:opacity-90">
								<EditIcon sx={{ fontSize: 18 }}/>
							</button>
						</TooltipTrigger>
						<TooltipContent>Edit dare</TooltipContent>
					</Tooltip>
				</div>)}

			<ArtworkFrame src={image} alt={dare.title} className="aspect-[16/10]" imageClassName="transition-transform duration-500 group-hover:scale-105">
				<div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent opacity-70"/>

				{dare.status === CreatorEventFulfilledDareStatus.Pending && (<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/70">
						<span className="inline-flex items-center gap-1.5 rounded-md border border-solid border-neon-amber/40 bg-neon-amber/15 px-2.5 py-1 text-xs font-semibold text-neon-amber">
							<ScheduleIcon sx={{ fontSize: 14 }}/>
							Pending review
						</span>
					</div>)}
				{dare.status === CreatorEventFulfilledDareStatus.Disabled && (<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/70">
						<span className="inline-flex items-center gap-1.5 rounded-md border border-solid border-neon-pink/40 bg-neon-pink/15 px-2.5 py-1 text-xs font-semibold text-neon-pink">
							<BlockIcon sx={{ fontSize: 14 }}/>
							Disabled
						</span>
					</div>)}
				{projectLabel && (<span className={cn('absolute left-2.5 top-2.5 z-10 inline-flex items-center rounded-md border border-solid px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider backdrop-blur-md', projectLabel.className)}>
						{projectLabel.label}
					</span>)}
			</ArtworkFrame>

			<div className="flex flex-1 flex-col p-4">
				<div className="flex items-center gap-2">
					<div className="relative shrink-0">
						<Img src={buildAssetUrl(dare.creator.image, { w: assetSize('thumbnail'), q: 80, fmt: 'webp' })} alt={dare.creator.displayName} className="h-7 w-7 rounded-full border border-solid border-white/20 object-cover"/>
						<span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-white">
							<PlatformIcon platform={dare.creator.platform} color={PlatformIconColor.Dark} size="sm"/>
						</span>
					</div>
					<h4 className="line-clamp-1 text-sm font-bold text-foreground">{dare.title}</h4>
				</div>
				<p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{dare.description}</p>

				{((_b = (_a = dare.platforms) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 && (<ul className="mt-2 flex items-center gap-1.5">
						{dare.platforms.map(platform => (<li key={platform} className="flex h-5 w-5 items-center justify-center rounded-full bg-white">
								<DarePlatformIcon size="12" platform={platform} color={PlatformIconColor.Dark}/>
							</li>))}
					</ul>)}

				{dare.authPlatform && (<p className="mt-2 text-[11px] italic text-muted-foreground/80">
						Requires a {getReadableSocialPlatform(dare.authPlatform)} account.
					</p>)}

				<div className="mt-auto pt-4">
					{canBeAccepted ? (<Button variant="brand" loading={accept.loading} success={accept.success} error={accept.error} onClick={onAccept} className="w-full rounded-lg text-xs font-semibold">
							Accept Dare
						</Button>) : (<div className="h-9" aria-hidden="true"/>)}
				</div>
			</div>
		</article>);
});
